import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const STATUS_COLOR = {
    open:        { bg:'rgba(59,130,246,0.12)',  color:'#60a5fa',  label:'Open' },
    in_progress: { bg:'rgba(234,179,8,0.12)',   color:'#fbbf24',  label:'In Progress' },
    resolved:    { bg:'rgba(34,197,94,0.12)',   color:'#4ade80',  label:'Resolved' },
    closed:      { bg:'rgba(100,116,139,0.12)', color:'#94a3b8',  label:'Closed' },
};

const STATUS_OPTIONS = ['open','in_progress','resolved','closed'];

function formatDate(d) {
    return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
}

const s = {
    badge: (status) => ({ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, background:STATUS_COLOR[status]?.bg, color:STATUS_COLOR[status]?.color }),
    btn:   { padding:'9px 18px', background:'#1d4ed8', color:'#fff', border:'none', borderRadius:7, fontWeight:700, fontSize:13, cursor:'pointer' },
    inp:   { width:'100%', padding:'10px 12px', border:'1px solid #d1d5db', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
    msg:   (isAdmin) => ({
        maxWidth:'78%', padding:'10px 14px', borderRadius:10, fontSize:14, lineHeight:1.55,
        background: isAdmin ? '#1d4ed8' : '#f3f4f6',
        color: isAdmin ? '#fff' : '#1a1d23',
        alignSelf: isAdmin ? 'flex-end' : 'flex-start',
        whiteSpace: 'pre-wrap',
    }),
    msgMeta: (isAdmin) => ({ fontSize:11, color:'#94a3b8', marginTop:3, textAlign: isAdmin ? 'right' : 'left' }),
};

// ─── Ticket Detail + Reply ────────────────────────────────────────────────────
function TicketDetail({ ticketId, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/api/tickets/${ticketId}`);
            setData(res.data);
        } catch {}
        setLoading(false);
    }, [ticketId]);

    useEffect(() => { load(); }, [load]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await api.post(`/api/tickets/${ticketId}/reply`, { message: reply.trim() });
            setReply('');
            load();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const toggleUnread = async () => {
        const newVal = !data?.ticket?.admin_unread;
        try {
            await api.patch(`/api/tickets/${ticketId}/unread`, { admin_unread: newVal });
            load();
        } catch {}
    };

    const changeStatus = async (status) => {
        setStatusLoading(true);
        try {
            await api.patch(`/api/tickets/${ticketId}/status`, { status });
            load();
        } catch {}
        setStatusLoading(false);
    };

    // Quick refund reply template
    const applyTemplate = (type) => {
        const templates = {
            refund: `Hi ${data?.ticket?.client_username || 'there'},\n\nThank you for reaching out. We've reviewed your order and processed a full refund to your account balance. It should be available immediately.\n\nPlease don't hesitate to contact us if you have any other questions.\n\nBest regards,\nOrbitGrowth Support`,
            no_refund: `Hi ${data?.ticket?.client_username || 'there'},\n\nThank you for contacting us. We've reviewed your request carefully.\n\nUnfortunately, as stated in our Terms of Service, we are unable to issue refunds for completed or partially completed orders. Your order was processed according to the specifications provided.\n\nIf you have any other questions, we're here to help.\n\nBest regards,\nOrbitGrowth Support`,
            investigating: `Hi ${data?.ticket?.client_username || 'there'},\n\nThank you for reaching out. We've received your request and our team is currently investigating the issue with your order.\n\nWe'll update you as soon as we have more information — usually within a few hours.\n\nBest regards,\nOrbitGrowth Support`,
            wrong_link: `Hi ${data?.ticket?.client_username || 'there'},\n\nThank you for reaching out. Unfortunately we cannot process orders with an invalid or private link. Please make sure:\n\n• The Instagram account/post is public\n• The link is correctly formatted (e.g. https://www.instagram.com/p/ABC123/)\n\nFeel free to place a new order with the correct link and we'll process it right away.\n\nBest regards,\nOrbitGrowth Support`,
        };
        setReply(templates[type] || '');
    };

    if (loading) return <div style={{ padding:20, color:'#64748b' }}>Loading...</div>;
    if (!data) return <div style={{ padding:20, color:'#ef4444' }}>Ticket not found.</div>;

    const { ticket, messages } = data;

    return (
        <div>
            <button onClick={onBack} style={{ background:'none', border:'none', color:'#1d4ed8', cursor:'pointer', fontWeight:600, fontSize:14, marginBottom:16, padding:0 }}>
                ← All Tickets
            </button>

            {/* Header */}
            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'20px 24px', marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                    <div>
                        <div style={{ fontSize:18, fontWeight:700, color:'#1a1d23', marginBottom:4 }}>{ticket.subject}</div>
                        <div style={{ fontSize:13, color:'#64748b' }}>
                            From <strong>{ticket.client_username}</strong> ({ticket.client_email}) · #{ticket.id} · {formatDate(ticket.created_at)}
                        </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <button onClick={toggleUnread}
                            style={{ padding:'5px 12px', borderRadius:6, border: ticket.admin_unread ? '2px solid #f59e0b' : '1px solid #d1d5db',
                                background: ticket.admin_unread ? 'rgba(245,158,11,0.12)' : '#f9fafb', fontSize:12, fontWeight:700, cursor:'pointer',
                                color: ticket.admin_unread ? '#d97706' : '#6b7280' }}>
                            {ticket.admin_unread ? '!! Da Leggere' : 'Segna Da Leggere'}
                        </button>
                        <span style={s.badge(ticket.status)}>{STATUS_COLOR[ticket.status]?.label || ticket.status}</span>
                        <select
                            value={ticket.status}
                            disabled={statusLoading}
                            onChange={e => changeStatus(e.target.value)}
                            style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #d1d5db', fontSize:13, cursor:'pointer' }}
                        >
                            {STATUS_OPTIONS.map(st => <option key={st} value={st}>{STATUS_COLOR[st].label}</option>)}
                        </select>
                    </div>
                </div>

                {ticket.order_id && (
                    <div style={{ marginTop:12, padding:'10px 14px', background:'#f8fafc', borderRadius:8, fontSize:13, color:'#475569' }}>
                        <strong>Order #{ticket.order_id}</strong> — {ticket.order_link} &nbsp;|&nbsp;
                        Qty: {ticket.order_quantity} &nbsp;|&nbsp;
                        Status: <strong>{ticket.order_status}</strong> &nbsp;|&nbsp;
                        Price: €{parseFloat(ticket.order_price || 0).toFixed(2)}
                    </div>
                )}
            </div>

            {/* Messages */}
            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'20px 24px', marginBottom:12 }}>
                <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
                    {messages.map(m => (
                        <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: m.is_admin ? 'flex-end' : 'flex-start' }}>
                            <div style={s.msg(m.is_admin)}>{m.message}</div>
                            <div style={s.msgMeta(m.is_admin)}>
                                {m.is_admin ? '⚡ OrbitGrowth Staff' : `👤 ${m.sender_username}`} · {formatDate(m.created_at)}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Quick templates */}
                <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:12, color:'#94a3b8', marginBottom:6, fontWeight:600 }}>QUICK TEMPLATES</div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {[
                            { key:'refund',       label:'✅ Refund approved' },
                            { key:'no_refund',    label:'❌ No refund' },
                            { key:'investigating',label:'🔍 Investigating' },
                            { key:'wrong_link',   label:'🔗 Wrong link' },
                        ].map(t => (
                            <button key={t.key} onClick={() => applyTemplate(t.key)}
                                style={{ padding:'5px 12px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:6, fontSize:12, cursor:'pointer', color:'#374151', fontWeight:600 }}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Reply box */}
                <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    placeholder="Type your reply..."
                    style={{ ...s.inp, minHeight:120, resize:'vertical', marginBottom:10 }}
                />
                <div style={{ display:'flex', gap:8 }}>
                    <button style={s.btn} onClick={sendReply} disabled={sending || !reply.trim()}>
                        {sending ? 'Sending...' : '✉️ Send Reply'}
                    </button>
                    <button onClick={() => { sendReply(); setTimeout(() => changeStatus('resolved'), 500); }}
                        disabled={sending || !reply.trim()}
                        style={{ ...s.btn, background:'#16a34a' }}>
                        ✅ Reply & Resolve
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Admin Tickets Page ──────────────────────────────────────────────────
export default function AdminTickets() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('open');
    const [selectedId, setSelectedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = statusFilter !== 'all' ? `?status=${statusFilter}&limit=100` : '?limit=100';
            const res = await api.get(`/api/tickets${params}`);
            setTickets(res.data.tickets || []);
        } catch {}
        setLoading(false);
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    if (selectedId) return <TicketDetail ticketId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;

    const openCount = tickets.filter(t => t.status === 'open').length;
    const unreadCount = tickets.filter(t => t.admin_unread).length;

    return (
        <div style={{ maxWidth:900, margin:'0 auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
                <div>
                    <h1 style={{ fontSize:22, fontWeight:800, color:'#1a1d23', marginBottom:4 }}>
                        Support Tickets
                        {unreadCount > 0 && <span style={{ fontSize:14, background:'#f59e0b', color:'#fff', borderRadius:20, padding:'2px 10px', marginLeft:8, verticalAlign:'middle' }}>{unreadCount} da leggere</span>}
                        {openCount > 0 && <span style={{ fontSize:14, background:'#ef4444', color:'#fff', borderRadius:20, padding:'2px 10px', marginLeft:8, verticalAlign:'middle' }}>{openCount} open</span>}
                    </h1>
                    <p style={{ color:'#64748b', fontSize:14 }}>{tickets.length} ticket{tickets.length !== 1 ? 's' : ''} shown</p>
                </div>
                <div style={{ display:'flex', gap:6 }}>
                    {['open','in_progress','resolved','closed','all'].map(st => (
                        <button key={st} onClick={() => setStatusFilter(st)}
                            style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #e5e7eb', fontSize:13, cursor:'pointer', fontWeight:600,
                                background: statusFilter === st ? '#1d4ed8' : '#fff',
                                color: statusFilter === st ? '#fff' : '#374151' }}>
                            {st === 'all' ? 'All' : STATUS_COLOR[st]?.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Loading...</div>
            ) : tickets.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
                    <div style={{ fontSize:16, fontWeight:600 }}>No {statusFilter !== 'all' ? statusFilter.replace('_',' ') : ''} tickets</div>
                </div>
            ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {tickets.map(t => (
                        <div key={t.id}
                            onClick={() => setSelectedId(t.id)}
                            style={{ background:'#fff', border: t.admin_unread ? '2px solid #f59e0b' : t.status==='open' ? '1px solid #93c5fd' : '1px solid #e5e7eb',
                                borderRadius:10, padding:'16px 20px', cursor:'pointer', transition:'box-shadow .15s' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                                <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontWeight:700, fontSize:15, color:'#1a1d23', marginBottom:3, display:'flex', alignItems:'center', gap:6 }}>
                                        {t.admin_unread && <span style={{ display:'inline-block', width:9, height:9, borderRadius:'50%', background:'#f59e0b', flexShrink:0 }} title="Da Leggere" />}
                                        #{t.id} — {t.subject}
                                    </div>
                                    <div style={{ fontSize:12, color:'#94a3b8' }}>
                                        👤 {t.client_username} ({t.client_email}) · {formatDate(t.created_at)}
                                        {t.order_id && ` · Order #${t.order_id}`}
                                        · {t.message_count} message{t.message_count !== '1' ? 's' : ''}
                                    </div>
                                    {t.last_message && (
                                        <div style={{ fontSize:13, color:'#64748b', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:600 }}>
                                            {t.last_message}
                                        </div>
                                    )}
                                </div>
                                <span style={s.badge(t.status)}>{STATUS_COLOR[t.status]?.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
